import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTenant } from "../../Contexts/TenantContext";
import { useAuth } from "../../Contexts/AuthContext";
import { useCart } from "../../Contexts/CartContext";
import { productService } from "../../Api/Services/productService";
import { SimplifiedUserProduct } from "../../Models/Product";
import { formatPriceBRL, unformat } from "../../Utils/DataFormatters";
import { FaSearch, FaTimes, FaUser, FaShoppingCart, FaShoppingBag, FaHome, FaWhatsapp } from "react-icons/fa";
import CartSidebar from "../CartSidebar/CartSidebar";
import MobileSidebar from "../MobileSidebar/MobileSidebar";
import UserAccountMenu from "../UserAccountMenu/UserAccountMenu";
import CartAuthModal from "../CartAuthModal/CartAuthModal";

const toTitleCase = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

interface AltNavbarProps {
  transparent?: boolean;
}

const AltNavbar = ({ transparent = false }: AltNavbarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const activeCategoryId = new URLSearchParams(location.search).get("categoria");
  const { clearCart, cartItems } = useCart();
  const { tenant, categories, tenantContact } = useTenant();
  const { user, logout } = useAuth();

  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SimplifiedUserProduct[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);
  const [isNavCartAuthOpen, setIsNavCartAuthOpen] = useState(false);
  const [navCartAuthTab, setNavCartAuthTab] = useState<'login' | 'register'>('login');

  const searchInputRef = useRef<HTMLInputElement>(null);
  const accountTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const whatsappLink = `https://wa.me/${unformat(tenantContact.whatsAppNumber || "")}?text=${encodeURIComponent("Olá, gostaria de ajuda!")}`;

  const isContactRestricted = user?.requiresContactInfoUpdate;
  const isFullyAuthenticated = !!user && !isContactRestricted;
  const isCartPage = location.pathname === "/carrinho";

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery("");
      setSearchResults([]);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await productService.getAllActive({ page: 1, pageSize: 8, search: searchQuery });
        setSearchResults(data.items);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchQuery]);

  const handleLogout = async () => {
    const onCartPage = location.pathname.includes("/carrinho");
    if (!onCartPage) {
      clearCart();
    }
    await logout();
    if (!onCartPage) {
      navigate("/");
    }
  };

  const navLinks = [
    { path: "/sobre", label: "Sobre" },
    { path: "/contato", label: "Contato" },
  ];

  const filteredCategories = categories.filter(
    (c) => !c.name.toLowerCase().includes("orçamento")
  );

  /**
   * The desktop nav grows with the tenant's category count, so a fixed `md`
   * breakpoint isn't enough — with many categories the row overflows (and forces
   * a horizontal scrollbar) long before 768px is reached. Instead we measure the
   * width the desktop row actually needs and fall back to the mobile layout as
   * soon as the viewport can't fit it.
   */
  const desktopNavRef = useRef<HTMLElement>(null);
  const layoutSignature = `${filteredCategories.length}|${navLinks.length}|${transparent}|${isCartPage}`;
  const [measurement, setMeasurement] = useState<{ signature: string; width: number } | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1920
  );

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMeasurementCurrent = measurement !== null && measurement.signature === layoutSignature;
  const desktopFits = !isMeasurementCurrent || viewportWidth >= measurement!.width;
  const showDesktopNav = viewportWidth >= 768 && desktopFits;

  useLayoutEffect(() => {
    const nav = desktopNavRef.current;
    if (!showDesktopNav || !nav) {
      return;
    }

    const measure = () => {
      const styles = window.getComputedStyle(nav);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const groups = Array.from(nav.children) as HTMLElement[];
      // Groups never shrink below their content, so their widths stay natural
      // even while the row is overflowing.
      const content = groups.reduce((sum, el) => sum + el.offsetWidth, 0);
      const minGap = 48;
      const required = Math.ceil(padding + content + minGap * Math.max(groups.length - 1, 0));
      setMeasurement((prev) =>
        prev && prev.signature === layoutSignature && prev.width === required
          ? prev
          : { signature: layoutSignature, width: required }
      );
    };

    measure();

    // Re-measure when a group resizes (logo image finishing loading, fonts, ...).
    const observer = new ResizeObserver(measure);
    Array.from(nav.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [showDesktopNav, layoutSignature]);

  // Drop mobile-only panels when the desktop row takes over again.
  useEffect(() => {
    if (showDesktopNav) {
      setIsMobileMenuOpen(false);
      setIsMobileUserMenuOpen(false);
    } else {
      setIsSearchOpen(false);
      setIsAccountOpen(false);
    }
  }, [showDesktopNav]);

  return (
    <>
      {/* Desktop nav */}
      <nav ref={desktopNavRef} className={`${showDesktopNav ? "flex" : "hidden"} items-center justify-between px-7 md:px-16 py-5 z-40 ${
        transparent
          ? "absolute top-0 left-0 right-0 bg-transparent [filter:drop-shadow(0_1px_4px_rgba(0,0,0,0.5))]"
          : "relative bg-white border-b shadow-sm"
      }`}>
        {/* Logo */}
        <Link to="/" className="shrink-0">
          {tenant?.logo ? (
            <img src={tenant.logo.url} alt="Logo" className={`object-contain ${transparent ? "h-14 lg:h-16" : "h-10 lg:h-12"}`} />
          ) : (
            <span className={`text-xl font-bold ${transparent ? "text-white" : "text-primary-600"}`}>{tenant?.companyName}</span>
          )}
        </Link>

        {/* Center nav links */}
        <ul className={`flex items-center gap-6 text-sm font-semibold ${transparent ? "text-white" : "text-black"}`}>
          {navLinks.map((link) => (
            <li key={link.path}>
              <Link
                to={link.path}
                className={`transition-colors hover:underline underline-offset-4 ${
                  transparent ? "hover:text-gray-200" : "hover:text-primary-600"
                } ${location.pathname === link.path ? (transparent ? "text-gray-200 underline" : "text-primary-600 underline") : ""}`}
              >
                {link.label}
              </Link>
            </li>
          ))}
          {filteredCategories.map((cat) => {
            const isActive = activeCategoryId === cat.id;
            return (
              <li key={cat.id}>
                <button
                  onClick={() => navigate(`/produtos?categoria=${cat.id}`)}
                  className={`transition-colors hover:underline underline-offset-4 ${
                    transparent ? "hover:text-gray-200" : "hover:text-primary-600"
                  } ${isActive ? (transparent ? "text-gray-200 underline" : "text-primary-600 underline") : ""}`}
                >
                  {toTitleCase(cat.name)}
                </button>
              </li>
            );
          })}
        </ul>

        {/* Right icons */}
        <div className="flex items-center gap-8 shrink-0">
          <button
            onClick={() => setIsSearchOpen((v) => !v)}
            className={`flex items-center justify-center w-5 h-5 transition-colors ${transparent ? "text-white hover:text-gray-200" : "text-black hover:text-primary-600"}`}
            aria-label="Pesquisar"
          >
            <FaSearch size={18} />
          </button>

          {/* Account */}
          <div
            className="relative"
            onMouseEnter={() => {
              if (accountTimeoutRef.current) {
                clearTimeout(accountTimeoutRef.current);
              }
              setIsAccountOpen(true);
            }}
            onMouseLeave={() => {
              accountTimeoutRef.current = setTimeout(() => setIsAccountOpen(false), 200);
            }}
          >
            <button
              className={`flex items-center justify-center w-5 h-5 transition-colors ${transparent ? "text-white hover:text-gray-200" : "text-black hover:text-primary-600"}`}
              aria-label="Conta"
            >
              <FaUser size={18} />
            </button>
            {isAccountOpen && (
              <div className="absolute -right-6 top-full mt-4 w-44 bg-white shadow-xl rounded-md border z-50">
                <div className="absolute -top-2 right-6 w-4 h-4 bg-white rotate-45 border-l border-t border-gray-200" />
                <div className="overflow-hidden rounded-md">
                  <UserAccountMenu
                    onClose={() => setIsAccountOpen(false)}
                    onLogout={handleLogout}
                    onCartAuth={isCartPage ? (tab) => { setNavCartAuthTab(tab); setIsNavCartAuthOpen(true); } : undefined}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Cart */}
          {!isCartPage && (
            <button
              onClick={() => setIsCartOpen(true)}
              className={`relative flex items-center justify-center w-5 h-5 transition-colors ${transparent ? "text-white hover:text-gray-200" : "text-black hover:text-primary-600"}`}
              aria-label="Carrinho"
            >
              <FaShoppingCart size={18} />
              <span className="absolute top-1/2 -translate-y-1/2 left-full ml-1 bg-primary-600 text-white text-xs font-bold rounded px-1.5 leading-[18px] min-w-[18px] text-center">
                {cartItems.length}
              </span>
            </button>
          )}
        </div>
      </nav>

      {/* Search overlay — drops below nav when open */}
      {isSearchOpen && showDesktopNav && (
        <div className="relative z-30 bg-white border-b shadow-md px-7 md:px-16 py-3">
          <div className="relative max-w-3xl mx-auto">
            <div className="flex items-center gap-3 border border-gray-300 rounded-full px-4 py-2 bg-white">
              <FaSearch className="text-gray-400 shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="O que você está procurando?"
                className="flex-1 outline-none text-sm text-gray-700 bg-transparent"
              />
              <button onClick={() => setIsSearchOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            </div>
            {/* Results */}
            {searchQuery.trim() && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <p className="p-4 text-sm text-gray-500">Buscando...</p>
                ) : searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => { navigate(`/produtos/${product.id}`); setIsSearchOpen(false); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 text-left"
                    >
                      {product.mainImageUrl ? (
                        <img src={product.mainImageUrl} alt={product.name} className="w-10 h-10 object-cover rounded" />
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                          <FaShoppingBag className="text-gray-400 text-xs" />
                        </div>
                      )}
                      <span className="flex-1 text-sm text-gray-800">{product.name}</span>
                      <span className="text-sm font-semibold text-primary-600">{formatPriceBRL(product.price)}</span>
                    </button>
                  ))
                ) : (
                  <p className="p-4 text-sm text-gray-500">Nenhum produto encontrado.</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile nav */}
      <nav className={`${showDesktopNav ? "hidden" : "block"} z-40 ${
        transparent ? "absolute top-0 left-0 right-0 bg-transparent" : "relative bg-white border-b shadow-sm"
      }`}>
        {/* Top bar row */}
        {transparent ? (
          /* Home: all 5 items as siblings for equal gaps */
          <div className="flex items-center justify-between px-4 sm:px-7 py-3">
            <button
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="w-8 h-8 -ml-2 flex items-center justify-center text-white transition-opacity hover:opacity-75"
              aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              <svg viewBox="0 0 24 24" width="22" height="22">
                <rect x="3" y="5" width="18" height="2" rx="1" fill="currentColor"
                  style={{ transformOrigin: "12px 6px", transition: "transform 0.25s ease", transform: isMobileMenuOpen ? "rotate(45deg) translate(0px,6px)" : "none" }} />
                <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor"
                  style={{ transition: "opacity 0.25s ease", opacity: isMobileMenuOpen ? 0 : 1 }} />
                <rect x="3" y="17" width="18" height="2" rx="1" fill="currentColor"
                  style={{ transformOrigin: "12px 18px", transition: "transform 0.25s ease", transform: isMobileMenuOpen ? "rotate(-45deg) translate(0px,-6px)" : "none" }} />
              </svg>
            </button>
            <Link to="/" className="flex flex-col items-center gap-0.5 text-white/90 hover:text-white">
              <FaHome size={17} />
              <span className="text-[10px] font-medium">Início</span>
            </Link>
            <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-0.5 text-white/90 hover:text-white">
              <FaWhatsapp size={17} />
              <span className="text-[10px] font-medium">Contato</span>
            </a>
            {isFullyAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsMobileUserMenuOpen((v) => !v)}
                  className="flex flex-col items-center gap-0.5 text-white/90 hover:text-white"
                >
                  <FaUser size={17} />
                  <span className="text-[10px] font-medium">{user!.firstName?.split(" ")[0] ?? "Conta"}</span>
                </button>
                {isMobileUserMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setIsMobileUserMenuOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-lg shadow-xl border z-50 overflow-hidden">
                      <UserAccountMenu
                        onClose={() => setIsMobileUserMenuOpen(false)}
                        onLogout={handleLogout}
                        onCartAuth={isCartPage ? (tab) => { setNavCartAuthTab(tab); setIsNavCartAuthOpen(true); } : undefined}
                      />
                    </div>
                  </>
                )}
              </div>
            ) : isCartPage ? (
              <button type="button" onClick={() => { setNavCartAuthTab('login'); setIsNavCartAuthOpen(true); }} className="flex flex-col items-center gap-0.5 text-white/90 hover:text-white">
                <FaUser size={17} />
                <span className="text-[10px] font-medium">Entrar</span>
              </button>
            ) : (
              <Link to="/login" className="flex flex-col items-center gap-0.5 text-white/90 hover:text-white">
                <FaUser size={17} />
                <span className="text-[10px] font-medium">Entrar</span>
              </Link>
            )}
            {!isCartPage && (
              <button onClick={() => setIsCartOpen(true)} className="flex items-center gap-1 text-white transition-opacity hover:opacity-75">
                <FaShoppingCart size={17} />
                {cartItems.length > 0 && (
                  <span className="bg-primary-600 text-white text-xs font-bold rounded px-1.5 leading-[18px] min-w-[18px] text-center">
                    {cartItems.length}
                  </span>
                )}
              </button>
            )}
          </div>
        ) : (
          /* Other pages: hamburger + centered logo + cart */
          <div className="flex items-center justify-between px-4 sm:px-7 py-4">
            <button
              onClick={() => setIsMobileMenuOpen((v) => !v)}
              className="w-8 h-8 -ml-2 flex items-center justify-center shrink-0 text-black transition-opacity hover:opacity-75"
              aria-label={isMobileMenuOpen ? "Fechar menu" : "Abrir menu"}
            >
              <svg viewBox="0 0 24 24" width="22" height="22">
                <rect x="3" y="5" width="18" height="2" rx="1" fill="currentColor"
                  style={{ transformOrigin: "12px 6px", transition: "transform 0.25s ease", transform: isMobileMenuOpen ? "rotate(45deg) translate(0px,6px)" : "none" }} />
                <rect x="3" y="11" width="18" height="2" rx="1" fill="currentColor"
                  style={{ transition: "opacity 0.25s ease", opacity: isMobileMenuOpen ? 0 : 1 }} />
                <rect x="3" y="17" width="18" height="2" rx="1" fill="currentColor"
                  style={{ transformOrigin: "12px 18px", transition: "transform 0.25s ease", transform: isMobileMenuOpen ? "rotate(-45deg) translate(0px,-6px)" : "none" }} />
              </svg>
            </button>
            <Link to="/" className="absolute left-1/2 -translate-x-1/2">
              {tenant?.logo ? (
                <img src={tenant.logo.url} alt="Logo" className="h-11 object-contain" />
              ) : (
                <span className="font-bold text-primary-600">{tenant?.companyName}</span>
              )}
            </Link>
            <div className="flex items-center gap-4 shrink-0">
              {!isCartPage && (
                <button onClick={() => setIsCartOpen(true)} className="flex items-center gap-1 text-black transition-opacity hover:opacity-75">
                  <FaShoppingCart size={17} />
                  {cartItems.length > 0 && (
                    <span className="bg-primary-600 text-white text-xs font-bold rounded px-1.5 leading-[18px] min-w-[18px] text-center">
                      {cartItems.length}
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Home page (transparent): logo + search overlaid on hero */}
        {transparent && (
          <div className="flex flex-col items-center px-6 pt-2 pb-6 gap-6">
            <Link to="/">
              {tenant?.logo ? (
                <img src={tenant.logo.url} alt="Logo" className="h-16 object-contain drop-shadow-lg" />
              ) : (
                <span className="text-2xl font-bold text-white drop-shadow-lg">{tenant?.companyName}</span>
              )}
            </Link>
            <div className="w-full relative">
              <div className="flex items-center gap-2 rounded-full border border-white/80 bg-white/80 px-4 py-3 shadow-md backdrop-blur-sm">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && searchResults.length > 0) { navigate(`/produtos/${searchResults[0].id}`); setSearchQuery(""); } }}
                  placeholder="Fala uma pesquisa..."
                  className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder:text-gray-500"
                />
                <FaSearch className="shrink-0 text-primary-600/90" size={14} />
              </div>
              {searchQuery.trim() && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border max-h-72 overflow-y-auto z-50">
                  {isSearching ? (
                    <p className="p-4 text-sm text-gray-500">Buscando...</p>
                  ) : searchResults.length > 0 ? (
                    searchResults.map((product) => (
                      <button
                        key={product.id}
                        onClick={() => { navigate(`/produtos/${product.id}`); setSearchQuery(""); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 text-left"
                      >
                        {product.mainImageUrl ? (
                          <img src={product.mainImageUrl} alt={product.name} className="w-8 h-8 object-cover rounded" />
                        ) : (
                          <div className="w-8 h-8 bg-gray-100 rounded" />
                        )}
                        <span className="flex-1 text-sm text-gray-800">{product.name}</span>
                        <span className="text-sm font-semibold text-primary-600">{formatPriceBRL(product.price)}</span>
                      </button>
                    ))
                  ) : (
                    <p className="p-4 text-sm text-gray-500">Nenhum produto encontrado.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Other pages: always-visible search bar below top bar */}
        {!transparent && (
          <div className="px-4 sm:px-7 pt-1 pb-4 relative">
            <div className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2.5">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && searchResults.length > 0) { navigate(`/produtos/${searchResults[0].id}`); setSearchQuery(""); } }}
                placeholder="O que você está procurando?"
                className="flex-1 outline-none text-sm text-gray-700 bg-transparent"
              />
              <FaSearch className="text-gray-400 shrink-0" size={13} />
            </div>
            {searchQuery.trim() && (
              <div className="absolute left-4 right-4 sm:left-7 sm:right-7 top-full -mt-3 bg-white rounded-lg shadow-xl border max-h-72 overflow-y-auto z-50">
                {isSearching ? (
                  <p className="p-4 text-sm text-gray-500">Buscando...</p>
                ) : searchResults.length > 0 ? (
                  searchResults.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => { navigate(`/produtos/${product.id}`); setSearchQuery(""); }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 text-left"
                    >
                      {product.mainImageUrl ? (
                        <img src={product.mainImageUrl} alt={product.name} className="w-8 h-8 object-cover rounded" />
                      ) : (
                        <div className="w-8 h-8 bg-gray-100 rounded" />
                      )}
                      <span className="flex-1 text-sm text-gray-800">{product.name}</span>
                      <span className="text-sm font-semibold text-primary-600">{formatPriceBRL(product.price)}</span>
                    </button>
                  ))
                ) : (
                  <p className="p-4 text-sm text-gray-500">Nenhum produto encontrado.</p>
                )}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Mobile slide-out menu */}
      <MobileSidebar
        isOpen={isMobileMenuOpen}
        breakpoint="none"
        onClose={() => setIsMobileMenuOpen(false)}
        navLinks={navLinks}
        filteredCategories={filteredCategories}
        handleLogout={handleLogout}
      />

      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
      <CartAuthModal
        isOpen={isNavCartAuthOpen}
        onClose={() => setIsNavCartAuthOpen(false)}
        onAuthSuccess={() => setIsNavCartAuthOpen(false)}
        initialTab={navCartAuthTab}
      />
    </>
  );
};

export default AltNavbar;
